/**
 * Video Pin Section - 重构版本
 * 视频固定模块，支持滚动动画、品牌轮播、数字统计等功能
 * 
 * 架构设计：
 * - 模块化：每个功能独立模块
 * - 统一配置：集中管理配置项
 * - 性能优化：RAF、缓存、IntersectionObserver
 * - 清晰的职责分离
 */

(function () {
  'use strict';

  // ==================== 配置 ====================
  const CONFIG = {
    mobileBreakpoint: 768,
    layoutCacheDuration: 100, // ms
    visibilityCacheDuration: 200, // ms
    carouselSpeed: {
      mobile: 60, // px/s
      desktop: 60  // px/s
    },
    textScroll: {
      mobile: { maxOffset: 0.6, opacityChange: 0.6 },
      desktop: { maxOffset: 0.3, opacityChange: 0.4 }
    },
    contentBlocks: {
      startProgress: 0.05,
      endProgress: 0.95
    },
    stageReveal: {
      carouselFadeStart: 0.12,
      carouselFadeEnd: 0.3,
      statsRevealThreshold: 0.12,
      carouselOffsetY: 18,
      statsOffsetY: 10,
      firstSectionFadeEnd: 0.26,
      forceFirstHideWhenCarouselOpacity: 0.45
    },
    stats: {
      startProgress: 0.2,
      defaultDuration: 0.8
    }
  };

  // ==================== 工具函数 ====================
  const Utils = {
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    isMobile() {
      return window.innerWidth <= CONFIG.mobileBreakpoint;
    },

    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    },

    getScrollY() {
      return (typeof lenisInstance !== 'undefined' && lenisInstance?.scroll !== undefined)
        ? lenisInstance.scroll
        : (window.pageYOffset || document.documentElement.scrollTop || 0);
    },

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    },

    clamp(value, min, max) {
      return Math.min(max, Math.max(min, value));
    },

    // 节流函数：限制函数执行频率
    throttle(func, limit) {
      let inThrottle;
      return function (...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    }
  };

  // ==================== Lenis 集成 ====================
  class LenisManager {
    constructor() {
      this.instance = null;
    }

    async init() {
      if (typeof Lenis === 'undefined') {
        await Utils.delay(100);
        return this.init();
      }

      this.instance = new Lenis({
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
      });

      // 与 ScrollTrigger 集成
      if (typeof ScrollTrigger !== 'undefined') {
        this.instance.on('scroll', () => ScrollTrigger.update());
        ScrollTrigger.scrollerProxy(document.body, {
          scrollTop: (value) => {
            if (arguments.length) {
              this.instance.scrollTo(value, { immediate: true });
            } else {
              return this.instance.scroll;
            }
          },
          getBoundingClientRect: () => ({
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight
          })
        });
      }

      // RAF 循环
      const raf = (time) => {
        this.instance.raf(time);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);

      document.documentElement.classList.add('lenis');
      this.resetScrollPosition();
    }

    resetScrollPosition() {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (this.instance) {
        this.instance.scrollTo(0, { immediate: true });
      }
    }
  }

  // ==================== 首屏文字滚动 ====================
  class FirstSectionTextScroll {
    constructor(section) {
      this.section = section;
      this.textContainer = section?.querySelector('.mt-text-pin-container');
      this.textBlocks = Array.from(section?.querySelectorAll('.mt-text-pin-content-block') || []);
      this.activeBlockIndex = -1;
      this.pendingBlockIndex = -1;
      this.isSwitchingBlock = false;
      this.switchStartTimer = null;
      this.switchEndTimer = null;
      this.isActive = false;
      this.rafId = null;
      this.lastTransformY = null;
      this.cachedViewportHeight = window.innerHeight || 1;
      this.layoutCacheTime = 0;
      this.observer = null;
    }

    init() {
      if (!this.textContainer) return;

      if (typeof gsap === 'undefined') {
        setTimeout(() => this.init(), 100);
        return;
      }

      this.setupStyles();
      this.setActiveBlock(0);
      this.setupObserver();
      if (this.isActive) {
        this.start();
      }
    }

    setActiveBlock(index) {
      if (!this.textBlocks.length) return;
      const nextIndex = Utils.clamp(index, 0, this.textBlocks.length - 1);
      if (nextIndex === this.activeBlockIndex) return;

      if (this.switchStartTimer) {
        clearTimeout(this.switchStartTimer);
        this.switchStartTimer = null;
      }
      if (this.switchEndTimer) {
        clearTimeout(this.switchEndTimer);
        this.switchEndTimer = null;
      }

      if (this.activeBlockIndex < 0) {
        this.textBlocks.forEach((blockEl, idx) => {
          blockEl.classList.toggle('is-active', idx === nextIndex);
        });
        this.activeBlockIndex = nextIndex;
        return;
      }

      this.pendingBlockIndex = nextIndex;
      this.isSwitchingBlock = true;
      this.textContainer?.classList.add('is-switching');

      this.switchStartTimer = setTimeout(() => {
        const targetIndex = this.pendingBlockIndex;
        this.textBlocks.forEach((blockEl, idx) => {
          blockEl.classList.toggle('is-active', idx === targetIndex);
        });
        this.activeBlockIndex = targetIndex;
      }, 60);

      this.switchEndTimer = setTimeout(() => {
        this.textContainer?.classList.remove('is-switching');
        this.isSwitchingBlock = false;
      }, 170);
    }

    updateContentBlock(progress) {
      if (this.textBlocks.length <= 1) return;
      const start = CONFIG.contentBlocks.startProgress;
      const end = CONFIG.contentBlocks.endProgress;
      const normalized = Utils.clamp((progress - start) / Math.max(0.001, end - start), 0, 0.9999);
      const index = Math.floor(normalized * this.textBlocks.length);
      this.setActiveBlock(index);
    }

    setupStyles() {
      this.textContainer.style.willChange = 'transform';
      this.textContainer.style.transition = 'none';
      this.textContainer.style.transform = 'translate3d(0,0,0)';
      this.textContainer.style.webkitTransform = 'translate3d(0,0,0)';
    }

    setupObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isActive = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
          if (this.isActive && !this.rafId) {
            this.start();
          } else if (!this.isActive && this.rafId) {
            this.stop();
          }
        });
      }, {
        root: null,
        rootMargin: '20% 0px',
        threshold: 0
      });
      this.observer.observe(this.section);
    }

    start() {
      if (this.rafId) return;
      this.update();
    }

    stop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    update() {
      if (!this.isActive || !this.section || !this.textContainer) {
        this.rafId = null;
        return;
      }

      const now = performance.now();
      if ((now - this.layoutCacheTime) > CONFIG.layoutCacheDuration) {
        this.cachedViewportHeight = window.innerHeight || 1;
        this.layoutCacheTime = now;
      }

      const scrollY = Utils.getScrollY();
      const sectionTop = this.section.offsetTop;
      const viewportHeight = this.cachedViewportHeight;
      const scrollEnd = sectionTop + viewportHeight;

      let progress = Utils.clamp((scrollY - 0) / (scrollEnd - 0), 0, 1);

      if (progress > 0) {
        const isMobile = Utils.isMobile();
        const config = CONFIG.textScroll[isMobile ? 'mobile' : 'desktop'];
        const eased = Utils.easeOutCubic(progress);
        const translateY = -eased * viewportHeight * config.maxOffset;

        if (this.lastTransformY !== translateY) {
          gsap.set(this.textContainer, {
            y: Math.round(translateY),
            force3D: true,
            overwrite: true
          });
          this.textContainer.style.transform = `translate3d(0,${Math.round(translateY)}px,0)`;
          this.textContainer.style.webkitTransform = `translate3d(0,${Math.round(translateY)}px,0)`;
          this.lastTransformY = translateY;
        }
      } else {
        if (this.lastTransformY !== 0) {
          gsap.set(this.textContainer, { y: 0, force3D: true, overwrite: true });
          this.textContainer.style.transform = 'translate3d(0,0,0)';
          this.textContainer.style.webkitTransform = 'translate3d(0,0,0)';
          this.lastTransformY = 0;
        }
      }

      this.updateContentBlock(progress);

      this.rafId = requestAnimationFrame(() => this.update());
    }

    destroy() {
      this.stop();
      if (this.switchStartTimer) {
        clearTimeout(this.switchStartTimer);
        this.switchStartTimer = null;
      }
      if (this.switchEndTimer) {
        clearTimeout(this.switchEndTimer);
        this.switchEndTimer = null;
      }
      this.textContainer?.classList.remove('is-switching');
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  // ==================== 品牌轮播 ====================
  class BrandCarousel {
    constructor(carouselWrapper, carouselTrack, direction) {
      this.carouselWrapper = carouselWrapper;
      this.carouselTrack = carouselTrack;
      this.direction = direction; // -1: 左, 1: 右
      this.items = carouselTrack.querySelectorAll('.mt-brand-carousel-item');
      this.baseItemCount = Math.max(1, Math.floor(this.items.length / 2));
      this.rafId = null;
      this.offset = 0;
      this.loopDistance = 0;
      this.lastX = null;
      this.cachedVisibility = true;
      this.visibilityCacheTime = 0;
    }

    async init() {
      if (this.items.length === 0) return;

      this.carouselTrack._baseItemCount = this.baseItemCount;
      await this.waitForImages();
      this.computeLoopDistance();
      this.startLoop();
      this.setupResizeHandler();
    }

    async waitForImages() {
      const images = this.carouselTrack.querySelectorAll('.mt-brand-image');
      if (images.length === 0) return;

      const loadPromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      });

      await Promise.all(loadPromises);
      await Utils.delay(100);
    }

    computeLoopDistance() {
      const style = getComputedStyle(this.carouselTrack);
      const gap = parseInt(style.gap || '0', 10);
      const viewportWidth = window.innerWidth || 1024;
      let allItems = this.carouselTrack.querySelectorAll('.mt-brand-carousel-item');
      const groupSize = this.baseItemCount;

      if (groupSize === 0) return;

      const baseItems = Array.from(allItems).slice(0, groupSize);
      const groupWidth = baseItems.reduce((w, item, idx) => {
        return w + item.offsetWidth + (idx < baseItems.length - 1 ? gap : 0);
      }, 0);

      if (groupWidth === 0) return;

      // 克隆足够的内容
      const minTotalWidth = Math.max(viewportWidth * 3, groupWidth * 3);
      const neededGroups = Math.ceil(minTotalWidth / groupWidth);
      const currentGroups = Math.floor(allItems.length / groupSize);

      for (let i = currentGroups; i < neededGroups && i < 20; i++) {
        baseItems.forEach(item => {
          this.carouselTrack.appendChild(item.cloneNode(true));
        });
        allItems = this.carouselTrack.querySelectorAll('.mt-brand-carousel-item');
      }

      this.loopDistance = groupWidth;
      this.offset = this.direction > 0 ? -this.loopDistance : 0;
      this.updateTransform();
    }

    updateTransform() {
      const x = Math.round(this.offset);
      if (this.lastX !== x) {
        this.carouselTrack.style.transform = `translate3d(${x}px,0,0)`;
        this.carouselTrack.style.webkitTransform = `translate3d(${x}px,0,0)`;
        this.carouselTrack.style.willChange = 'transform';
        this.lastX = x;
      }
    }

    startLoop() {
      if (this.rafId) return;
      this.lastTs = null; // 重置时间戳
      this.rafId = requestAnimationFrame((t) => this.loop(t));
    }

    loop(ts) {
      if (!this.lastTs) this.lastTs = ts;
      const dt = (ts - this.lastTs) / 1000;
      this.lastTs = ts;

      // 移动端可见性检查
      if (Utils.isMobile()) {
        const now = performance.now();
        if ((now - this.visibilityCacheTime) > CONFIG.visibilityCacheDuration) {
          const rect = this.carouselWrapper.getBoundingClientRect();
          const vh = window.innerHeight || 0;
          this.cachedVisibility = rect.bottom > -vh * 0.5 && rect.top < vh * 1.5;
          this.visibilityCacheTime = now;
        }
        if (!this.cachedVisibility) {
          this.rafId = requestAnimationFrame((t) => this.loop(t));
          return;
        }
      }

      const speed = CONFIG.carouselSpeed[Utils.isMobile() ? 'mobile' : 'desktop'];
      this.offset += this.direction * speed * dt;

      if (this.loopDistance === 0) {
        this.computeLoopDistance();
        if (this.loopDistance === 0) {
          this.rafId = requestAnimationFrame((t) => this.loop(t));
          return;
        }
      }

      // 循环重置
      if (this.loopDistance > 0) {
        if (this.direction < 0) {
          while (this.offset < -this.loopDistance) {
            this.offset += this.loopDistance;
          }
        } else {
          while (this.offset > 0) {
            this.offset -= this.loopDistance;
          }
        }
      }

      this.updateTransform();
      this.rafId = requestAnimationFrame((t) => this.loop(t));
    }

    setupResizeHandler() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.loopDistance = 0;
          this.computeLoopDistance();
        }, 250);
      });
    }

    destroy() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
    }
  }

  // ==================== 主控制器 ====================
  class VideoPinSectionController {
    constructor() {
      this.lenisManager = new LenisManager();
      this.sections = [];
      this.firstSectionTextScroll = null;
    }

    async init() {
      await this.waitForGSAP();
      await this.lenisManager.init();
      await Utils.delay(200);

      const sections = document.querySelectorAll('.mt-video-pin-section');
      sections.forEach((section, index) => {
        if (Utils.isMobile() && index > 0) {
          this.initSectionWhenVisible(section, index);
        } else {
          this.initSection(section, index);
        }
      });
    }

    async waitForGSAP() {
      while (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        await Utils.delay(100);
      }
      gsap.registerPlugin(ScrollTrigger);
    }

    initSectionWhenVisible(section, index) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            observer.disconnect();
            setTimeout(() => this.initSection(section, index), 100);
          }
        });
      }, {
        root: null,
        rootMargin: '50% 0px',
        threshold: 0
      });
      observer.observe(section);
    }

    initSection(section, index) {
      // 初始化首屏文字滚动
      if (section && section.matches('[data-first-section]')) {
        this.firstSectionTextScroll = new FirstSectionTextScroll(section);
        this.firstSectionTextScroll.init();
      }

      // 初始化品牌轮播
      this.initBrandCarousels(section);

      // 初始化滚轮控制图片
      this.initScrollControlledVideos(section);
    }

    initBrandCarousels(section) {
      const carouselWrappers = section.querySelectorAll('.mt-brand-carousel-wrapper');
      carouselWrappers.forEach(carouselWrapper => {
        // 初始化文字动画
        this.initBrandCarouselText(carouselWrapper);

        // 初始化轮播
        const track1 = carouselWrapper.querySelector('.mt-brand-carousel-track:not(.mt-brand-carousel-track-2)');
        const track2 = carouselWrapper.querySelector('.mt-brand-carousel-track-2');

        if (track1) {
          const carousel1 = new BrandCarousel(carouselWrapper, track1, -1);
          carousel1.init();
        }
        if (track2) {
          const carousel2 = new BrandCarousel(carouselWrapper, track2, 1);
          carousel2.init();
        }

        // 设置轮播过渡动画
        if (track1 && !track1.classList.contains('mt-brand-carousel-track-2')) {
          this.setupCarouselTransition(section, carouselWrapper);
        }
      });
    }

    initBrandCarouselText(carouselWrapper) {
      // 简化版：只处理基本逻辑
      const textContainer = carouselWrapper.querySelector('.mt-brand-carousel-text');
      if (!textContainer) return;

      const textContent = textContainer.querySelector('.mt-brand-carousel-text-content');
      if (!textContent) return;

      const text = textContainer.dataset.text || '';
      if (!text) return;

      const words = text.split(/\s+/).filter(w => w.trim());
      if (words.length === 0) return;

      textContent.innerHTML = words.map(w => `<span class="word">${w}</span>`).join(' ');

      // 初始化样式
      gsap.set(textContainer, { opacity: 0 });
      gsap.set(textContent.querySelectorAll('.word'), { opacity: 0, y: 20 });

      // 桌面端使用 ScrollTrigger，移动端使用 RAF（简化实现）
      if (Utils.isMobile()) {
        // 移动端简化实现
        this.initBrandCarouselTextMobile(carouselWrapper, textContainer, textContent.querySelectorAll('.word'), words.length);
      } else {
        this.initBrandCarouselTextDesktop(carouselWrapper, textContainer, textContent.querySelectorAll('.word'), words.length);
      }
    }

    initBrandCarouselTextDesktop(carouselWrapper, textContainer, wordElements, totalWords) {
      let scrollDirection = 1;
      let lastScrollTop = 0;

      ScrollTrigger.create({
        trigger: carouselWrapper,
        start: 'top bottom',
        end: 'top top',
        scrub: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const progress = self.progress;
          const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

          scrollDirection = currentScrollTop > lastScrollTop ? 1 : -1;
          lastScrollTop = currentScrollTop;

          let textOpacity = 0;
          if (progress >= 0.3) {
            textOpacity = Utils.clamp((progress - 0.3) / 0.4, 0, 1);
          }

          gsap.set(textContainer, { opacity: textOpacity });

          const baseWordProgress = Utils.clamp((progress - 0.3) / 0.4, 0, 1);

          wordElements.forEach((word, index) => {
            const wordStart = index / totalWords;
            const wordEnd = (index + 1) / totalWords;
            let wordOpacity = 0;

            if (scrollDirection > 0) {
              const wordShowProgress = baseWordProgress;
              if (wordShowProgress >= wordStart) {
                if (wordShowProgress >= wordEnd) {
                  wordOpacity = 1;
                } else {
                  wordOpacity = Utils.clamp((wordShowProgress - wordStart) / (wordEnd - wordStart) * 2.5, 0, 1);
                }
              }
            } else {
              const wordHideProgress = 1 - baseWordProgress;
              const reverseIndex = totalWords - 1 - index;
              const reverseWordStart = reverseIndex / totalWords;
              const reverseWordEnd = (reverseIndex + 1) / totalWords;

              if (wordHideProgress >= reverseWordStart) {
                if (wordHideProgress >= reverseWordEnd) {
                  wordOpacity = 0;
                } else {
                  wordOpacity = Math.max(0, 1 - (wordHideProgress - reverseWordStart) / (reverseWordEnd - reverseWordStart) * 2.5);
                }
              } else {
                wordOpacity = 1;
              }
            }

            const isIOS = Utils.isIOS();
            gsap.set(word, {
              opacity: wordOpacity * textOpacity,
              y: (1 - wordOpacity) * 20,
              force3D: isIOS,
              z: isIOS ? 0.01 : 0
            });
          });
        }
      });
    }

    initBrandCarouselTextMobile(carouselWrapper, textContainer, wordElements, totalWords) {
      // 移动端简化实现 - 使用 RAF
      let rafId = null;
      let isActive = true;
      let cachedRect = null;
      let cachedScrollY = null;
      let layoutCacheTime = 0;
      const wordStates = Array.from(wordElements).map(() => ({ opacity: -1, offsetY: -1 }));
      let lastContainerOpacity = -1;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          isActive = entry.isIntersecting || entry.boundingClientRect.bottom > -window.innerHeight;
          if (isActive && !rafId) {
            update();
          } else if (!isActive && rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        });
      }, {
        root: null,
        rootMargin: '0% 0px',
        threshold: 0
      });

      observer.observe(carouselWrapper);

      const update = () => {
        if (!isActive || !textContainer) {
          rafId = null;
          return;
        }

        const now = performance.now();
        const scrollY = Utils.getScrollY();
        const viewportHeight = window.innerHeight || 1;

        if (!cachedRect || !cachedScrollY || (now - layoutCacheTime) > 50) {
          cachedRect = carouselWrapper.getBoundingClientRect();
          cachedScrollY = scrollY;
          layoutCacheTime = now;
        }

        const sectionTop = cachedScrollY + cachedRect.top;
        const sectionHeight = cachedRect.height || viewportHeight;
        const center = cachedScrollY + viewportHeight * 0.5;
        let progress = Utils.clamp((center - sectionTop) / sectionHeight, 0, 1);

        let containerOpacity = 0;
        if (progress > 0.1) {
          containerOpacity = Utils.clamp((progress - 0.1) / 0.4, 0, 1);
        }

        if (Math.abs(lastContainerOpacity - containerOpacity) > 0.001) {
          textContainer.style.opacity = containerOpacity;
          lastContainerOpacity = containerOpacity;
        }

        const revealProgress = containerOpacity;
        wordElements.forEach((word, index) => {
          const perWordSpan = 1 / Math.max(1, totalWords);
          const start = perWordSpan * index;
          const end = perWordSpan * (index + 1);

          let local = 0;
          if (revealProgress >= end) {
            local = 1;
          } else if (revealProgress > start) {
            local = (revealProgress - start) / (end - start);
          }

          const eased = Utils.easeOutCubic(local);
          const opacity = eased * containerOpacity;
          const offsetY = (1 - eased) * 20;
          const roundedOffsetY = Math.round(offsetY);

          const state = wordStates[index];
          if (Math.abs(state.opacity - opacity) > 0.001 || state.offsetY !== roundedOffsetY) {
            word.style.opacity = opacity;
            word.style.transform = `translate3d(0,${roundedOffsetY}px,0)`;
            word.style.webkitTransform = `translate3d(0,${roundedOffsetY}px,0)`;
            state.opacity = opacity;
            state.offsetY = roundedOffsetY;
          }
        });

        rafId = requestAnimationFrame(update);
      };

      if (isActive) {
        update();
      }
    }

    setupCarouselTransition(section, carouselWrapper) {
      const isMobile = Utils.isMobile();
      const allSections = document.querySelectorAll('.mt-video-pin-section');
      const hasMultipleSections = allSections.length > 1;

      // 移动端多模块优化
      if (isMobile && hasMultipleSections) {
        carouselWrapper.classList.add('visible');
        carouselWrapper.style.opacity = '1';
        carouselWrapper.style.visibility = 'visible';

        const currentSectionIndex = Array.from(allSections).indexOf(section);
        if (currentSectionIndex > 0) {
          const videoPinWrapper = section.querySelector('.mt-video-pin-wrapper');
          if (videoPinWrapper) {
            videoPinWrapper.style.opacity = '0';
            videoPinWrapper.style.visibility = 'hidden';
          }
        }
        return;
      }

      // 预设置 will-change 优化性能
      if (carouselWrapper) {
        carouselWrapper.style.willChange = 'opacity, transform';
      }
      const videoPinWrapper = section.querySelector('.mt-video-pin-wrapper');
      if (videoPinWrapper) {
        videoPinWrapper.style.willChange = 'opacity';
      }

      // 缓存上一次的值，避免不必要的更新
      let lastProgress = -1;
      let lastCarouselOpacity = -1;
      let lastFirstSectionOpacity = -1;

      // 创建过渡动画
      const transitionTrigger = ScrollTrigger.create({
        id: `carousel-transition-${carouselWrapper.dataset.blockId}`,
        trigger: carouselWrapper,
        start: 'top bottom',
        end: 'top top',
        scrub: isMobile ? 0.3 : 0.5, // 更平滑的 scrub 值
        invalidateOnRefresh: true,
        anticipatePin: 1, // 提前准备 pin 操作
        onUpdate: (self) => {
          const progress = self.progress;

          // 节流：只在变化足够大时更新
          if (Math.abs(progress - lastProgress) < 0.001) return;
          lastProgress = progress;

          // 检查页面是否在顶部：如果滚动位置接近顶部，强制 videoPinWrapper 显示
          const scrollY = Utils.getScrollY();
          const isAtTop = scrollY < 50;

          // 如果页面在顶部，强制显示；否则根据 progress 线性淡出
          let firstSectionOpacity;
          if (isAtTop) {
            firstSectionOpacity = 1;
          } else {
            firstSectionOpacity = progress <= 0.5 ? Math.max(0, 1 - progress * 2) : 0;
          }

          if (Math.abs(firstSectionOpacity - lastFirstSectionOpacity) > 0.001) {
            if (videoPinWrapper) {
              gsap.set(videoPinWrapper, {
                opacity: firstSectionOpacity,
                visibility: firstSectionOpacity > 0 ? 'visible' : 'hidden',
                force3D: true,
                z: Utils.isIOS() ? 0.01 : 0.01
              });
            }
            lastFirstSectionOpacity = firstSectionOpacity;
          }

          if (progress < 0.5) {
            const carouselOpacity = Utils.clamp(progress / 0.5, 0, 1);

            // 只在值变化时更新
            if (Math.abs(carouselOpacity - lastCarouselOpacity) > 0.001) {
              carouselWrapper.classList.toggle('visible', carouselOpacity > 0);
              // 当品牌轮播透明度很低时，设置 pointer-events: none，让点击事件穿透到下面的内容
              const pointerEvents = carouselOpacity > 0.1 ? 'auto' : 'none';
              gsap.set(carouselWrapper, {
                opacity: carouselOpacity,
                y: 0,
                force3D: true,
                z: Utils.isIOS() ? 0.01 : 0.01
              });
              carouselWrapper.style.pointerEvents = pointerEvents;
              lastCarouselOpacity = carouselOpacity;
            }
          } else {
            if (lastCarouselOpacity !== 1) {
              carouselWrapper.classList.add('visible');
              carouselWrapper.style.pointerEvents = 'auto';
              gsap.set(carouselWrapper, { y: 0, force3D: true, z: Utils.isIOS() ? 0.01 : 0.01 });
              lastCarouselOpacity = 1;
            }
          }
        }
      });

      // 创建钉住效果
      const { pinTrigger, scrollVideoSection } = this.setupCarouselPin(section, carouselWrapper, transitionTrigger);

      // 设置初始状态
      requestAnimationFrame(() => {
        // 延迟 refresh，避免在初始化时频繁调用
        setTimeout(() => {
          ScrollTrigger.refresh();

          // 在 refresh 后再次检查，确保获取正确的 progress
          const initialProgress = transitionTrigger.progress;

          // 检查页面是否在顶部：如果滚动位置接近顶部，强制 videoPinWrapper 显示
          const scrollY = Utils.getScrollY();
          const isAtTop = scrollY < 50; // 如果滚动位置小于50px，认为在顶部

          // 初始状态：如果页面在顶部，videoPinWrapper 应该完全显示（opacity: 1）
          // 只有当滚动到品牌轮播开始淡入时，videoPinWrapper 才开始淡出
          const initialFirstSectionOpacity = isAtTop
            ? 1
            : (initialProgress <= 0.5 ? Math.max(0, 1 - initialProgress * 2) : 0);

          const initialCarouselOpacitySafe = initialProgress < 0.5
            ? Utils.clamp(initialProgress / 0.5, 0, 1)
            : 1;

          this.applyInitialState(section, carouselWrapper, scrollVideoSection, pinTrigger, transitionTrigger, initialFirstSectionOpacity, initialCarouselOpacitySafe);
        }, 100);
      });
    }

    applyInitialState(section, carouselWrapper, scrollVideoSection, pinTrigger, transitionTrigger, initialFirstSectionOpacity, initialCarouselOpacity) {
      carouselWrapper.classList.toggle('visible', initialCarouselOpacity > 0);

      // 设置初始 scale：PC端1.5倍，移动端1倍
      const isMobileScale = Utils.isMobile();
      const initialScale = initialCarouselOpacity > 0
        ? (isMobileScale ? 1 : 1.5)
        : (isMobileScale ? 1 : 1.5); // 即使opacity为0，也设置默认scale

      // 当品牌轮播透明度很低时，设置 pointer-events: none，让点击事件穿透
      const pointerEvents = initialCarouselOpacity > 0.1 ? 'auto' : 'none';
      gsap.set(carouselWrapper, {
        opacity: initialCarouselOpacity,
        scale: initialScale,
        force3D: true,
        z: Utils.isIOS() ? 0.01 : 0.01
      });
      // carouselWrapper.style.pointerEvents = pointerEvents;

      const videoPinWrapper = section.querySelector('.mt-video-pin-wrapper');
      if (videoPinWrapper) {
        gsap.set(videoPinWrapper, {
          opacity: initialFirstSectionOpacity,
          visibility: initialFirstSectionOpacity > 0 ? 'visible' : 'hidden',
          force3D: true,
          z: Utils.isIOS() ? 0.01 : 0.01
        });
      }

      // 设置 scrollVideoSection 的初始状态
      if (scrollVideoSection && pinTrigger) {
        const pinProgress = pinTrigger.progress;
        const transitionProgress = transitionTrigger.progress;

        let initialFinalOpacity = 0;
        if (transitionProgress < 0.5) {
          initialFinalOpacity = Utils.clamp(transitionProgress / 0.5, 0, 1);
        } else {
          initialFinalOpacity = Math.max(0, 1 - pinProgress);
        }

        let initialImageOpacity = 0;

        // 只有在 transitionTrigger 完成淡入后，才考虑显示 scrollVideoSection
        if (transitionProgress >= 0.5) {
          // 品牌轮播淡出时，scrollVideoSection 淡入；品牌轮播淡入时，scrollVideoSection 淡出
          initialImageOpacity = Utils.clamp(1 - initialFinalOpacity, 0, 1);
        }

        // 上下关系，只控制 opacity，不控制 z-index
        gsap.set(scrollVideoSection, {
          opacity: initialImageOpacity,
          visibility: initialImageOpacity > 0 ? 'visible' : 'hidden',
          force3D: true,
          z: Utils.isIOS() ? 0.01 : 0.01
        });
      }
    }

    setupCarouselPin(section, carouselWrapper, transitionTrigger) {
      const isMobile = Utils.isMobile();
      // 查找父容器（如果存在）或直接查找 scrollVideoSection
      const overlayContainer = section.querySelector('.mt-carousel-overlay-container');
      const scrollVideoSection = overlayContainer
        ? overlayContainer.querySelector('.mt-scroll-video-section')
        : section.querySelector('.mt-scroll-video-section');

      // 设置 scrollVideoSection 初始状态
      if (scrollVideoSection) {
        gsap.set(scrollVideoSection, {
          opacity: 0,
          visibility: 'visible',
          force3D: true,
          z: Utils.isIOS() ? 0.01 : 0.01
        });
      } else {
        console.warn('⚠️ setupCarouselPin: scrollVideoSection 未找到', {
          hasOverlayContainer: !!overlayContainer,
          section: section
        });
      }

      // 如果有父容器，pin 父容器；否则 pin 品牌轮播
      // 注意：pin 父容器可以保持两个子元素的重叠关系，同时确保 pin 功能正常
      const pinTarget = overlayContainer || carouselWrapper;

      // 预设置 will-change 优化性能
      if (scrollVideoSection) {
        scrollVideoSection.style.willChange = 'opacity';
      }
      if (pinTarget) {
        pinTarget.style.willChange = 'transform';
      }

      // 计算数字滚动的持续时间，用于调整 pin 的 end
      const statsSection = scrollVideoSection?.querySelector('.mt-scroll-stats-section');
      const statsDuration = statsSection
        ? (parseFloat(statsSection.dataset.statsDuration) || CONFIG.stats.defaultDuration)
        : CONFIG.stats.defaultDuration;

      // pin 的 end 需要覆盖：
      // 1. 品牌轮播的 pin 时间（2个视口高度）
      // 2. 数字滚动的持续时间（statsDuration 个视口高度）
      // 所以总共需要 2 + statsDuration 个视口高度
      const totalPinDuration = 2 + statsDuration;

      // 缓存上一次的值，避免不必要的更新
      let lastPinProgress = -1;
      let lastFinalOpacity = -1;
      let lastImageOpacity = -1;
      let lastScale = -1;

      const pinTrigger = ScrollTrigger.create({
        id: `carousel-pin-${carouselWrapper.dataset.blockId}`,
        trigger: pinTarget,
        start: 'top top',
        end: () => `+=${window.innerHeight * totalPinDuration}`, // 延长到数字滚动结束
        pin: pinTarget, // pin 父容器（包含两个重叠元素）或品牌轮播
        pinSpacing: true,
        scrub: isMobile ? 2 : 3, // 进一步增加 scrub 值，让动画更平滑，避免回弹
        invalidateOnRefresh: true,
        anticipatePin: 1, // 提前准备 pin 操作，减少卡顿a
        onUpdate: (self) => {
          const progress = self.progress;
          const transitionProgress = transitionTrigger?.progress ?? 1;

          // 节流：只在变化足够大时更新
          if (Math.abs(progress - lastPinProgress) < 0.001) return;
          lastPinProgress = progress;

          let finalOpacity = 0;
          if (transitionProgress < 0.5) {
            finalOpacity = Utils.clamp(transitionProgress / 0.5, 0, 1);
          } else {
            finalOpacity = Math.max(0, 1 - progress * 1.8);
          }

          const isMobileScale = Utils.isMobile();
          // 默认 scale：PC端1.5倍，移动端1倍
          // 当 finalOpacity > 0 时，随着滚动进度逐渐缩小（移动端从1缩小到0.9，PC端从1.5缩小到0.8）
          // 当 finalOpacity 为 0 时，保持默认放大值
          const defaultScale = isMobileScale ? 1 : 1.5;
          const scale = finalOpacity > 0
            ? (isMobileScale ? (1 - progress * 0.1) : (1.5 - progress * 0.7))
            : defaultScale;

          // 合并更新：只在值变化时更新
          const needsUpdate = Math.abs(finalOpacity - lastFinalOpacity) > 0.001 ||
            Math.abs(scale - lastScale) > 0.001;

          if (needsUpdate) {
            // 当品牌轮播透明度很低时，设置 pointer-events: none，让点击事件穿透到下面的内容
            const pointerEvents = finalOpacity > 0.1 ? 'auto' : 'none';
            gsap.set(carouselWrapper, {
              opacity: finalOpacity,
              scale: scale,
              force3D: true,
              z: Utils.isIOS() ? 0.01 : 0.01
            });
            // carouselWrapper.style.pointerEvents = pointerEvents;
            lastFinalOpacity = finalOpacity;
            lastScale = scale;
          }

          // 控制 scrollVideoSection 的淡入淡出：与品牌轮播反向变化，实现双向滚动效果
          if (scrollVideoSection) {
            let imageOpacity = 0;

            // 只有在 transitionTrigger 完成淡入后，才考虑显示 scrollVideoSection
            if (transitionProgress >= 0.5) {
              // 品牌轮播淡出时，scrollVideoSection 淡入；品牌轮播淡入时，scrollVideoSection 淡出
              // 直接反向：imageOpacity = 1 - finalOpacity
              const rawOpacity = Utils.clamp(1 - finalOpacity, 0, 1);
              const revealThresholdOpacity = Utils.clamp(CONFIG.stageReveal.statsRevealThreshold * 1.5, 0, 1);
              imageOpacity = rawOpacity <= revealThresholdOpacity
                ? 0
                : Utils.clamp((rawOpacity - revealThresholdOpacity) / Math.max(0.001, 1 - revealThresholdOpacity), 0, 1);
            }

            const imageOffsetY = (1 - imageOpacity) * CONFIG.stageReveal.statsOffsetY;

            // 只在值变化时更新
            if (Math.abs(imageOpacity - lastImageOpacity) > 0.001) {
              gsap.set(scrollVideoSection, {
                opacity: imageOpacity,
                visibility: imageOpacity > 0 ? 'visible' : 'hidden',
                yPercent: imageOffsetY,
                force3D: true,
                z: Utils.isIOS() ? 0.01 : 0.01
              });
              lastImageOpacity = imageOpacity;
            }
          }
        },
        onUnpin: () => {
          // pin 释放时，确保数字滚动保持最终状态，避免回弹
          if (scrollVideoSection) {
            const statsSection = scrollVideoSection.querySelector('.mt-scroll-stats-section');
            if (statsSection) {
              gsap.set(statsSection, {
                opacity: 1,
                yPercent: 0,
                force3D: true
              });
              // 移除 will-change 优化，避免回弹
              statsSection.style.willChange = 'auto';
            }
          }
        }
      });

      // 延迟 refresh，避免在创建时立即调用
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();

        // 设置初始 scale：PC端1.5倍，移动端1倍
        // 确保在页面刷新后第一次滚动到那里时，默认的放大效果生效
        const initialPinProgress = pinTrigger.progress;
        const initialTransitionProgress = transitionTrigger?.progress ?? 1;

        let initialFinalOpacity = 0;
        if (initialTransitionProgress < 0.5) {
          initialFinalOpacity = Utils.clamp(initialTransitionProgress / 0.5, 0, 1);
        } else {
          initialFinalOpacity = Math.max(0, 1 - initialPinProgress * 1.8);
        }

        const isMobileScale = Utils.isMobile();
        const defaultScale = isMobileScale ? 1 : 1.5;
        const initialScale = initialFinalOpacity > 0
          ? (isMobileScale ? (1 - initialPinProgress * 0.1) : (1.5 - initialPinProgress * 0.7))
          : defaultScale; // 如果 opacity 为 0，仍然设置默认 scale

        // 设置初始 scale，确保默认放大效果
        gsap.set(carouselWrapper, {
          scale: initialScale,
          force3D: true,
          z: Utils.isIOS() ? 0.01 : 0.01
        });
      });

      // 返回 pinTrigger 和 scrollVideoSection，用于设置初始状态
      return { pinTrigger, scrollVideoSection };
    }


    initScrollControlledVideos(section) {
      // 查找父容器内的 scrollVideoSection 或直接查找
      const overlayContainer = section.querySelector('.mt-carousel-overlay-container');
      const scrollVideoSections = overlayContainer
        ? overlayContainer.querySelectorAll('.mt-scroll-video-section')
        : section.querySelectorAll('.mt-scroll-video-section');

      if (!scrollVideoSections.length) return;

      scrollVideoSections.forEach(section => {
        // 不在这里设置 opacity，由 setupCarouselPin 统一控制
        // 只设置 visibility 和 force3D，不设置 opacity
        gsap.set(section, {
          visibility: 'visible',
          force3D: true,
          opacity: 0  // 初始隐藏，由 setupCarouselPin 控制淡入
        });

        const isMobile = Utils.isMobile();
        const image = section.querySelector(isMobile ? '.mt-scroll-image--mobile' : '.mt-scroll-image--desktop') ||
          section.querySelector('.mt-scroll-image') ||
          section.querySelector('img');
        if (!image) return;

        image.style.width = '100%';
        image.style.height = '100%';
        image.style.objectFit = 'cover';
        image.style.display = 'block';

        const parentSection = section.closest('.mt-video-pin-section');
        const carouselWrapper = parentSection?.querySelector('.mt-brand-carousel-wrapper');
        const statsSection = section.querySelector('.mt-scroll-stats-section');
        const statNumbers = statsSection?.querySelectorAll('.mt-scroll-stat-number') || [];

        if (!statsSection || !statNumbers.length) return;

        let lastStatsProgress = -1;
        let lastStatValues = [];
        let isStatsComplete = false; // 标记数字滚动是否已完成

        const updateStats = (scrollProgress) => {
          const statsStart = CONFIG.stats.startProgress;
          const statsDuration = parseFloat(statsSection.dataset.statsDuration) || CONFIG.stats.defaultDuration;
          const statsEnd = statsStart + statsDuration;

          let statsProgress = 0;
          if (scrollProgress >= statsStart) {
            statsProgress = scrollProgress >= statsEnd ? 1 : (scrollProgress - statsStart) / statsDuration;
          }
          statsProgress = Utils.clamp(statsProgress, 0, 1);

          // 记录上一帧进度，用于检测“最后一下”
          const prevStatsProgress = lastStatsProgress;

          // 如果已经完成，保持完成状态
          if (isStatsComplete && statsProgress >= 1) {
            return;
          }

          const progressThreshold = isMobile ? 0.001 : 0.0005;
          if (lastStatsProgress >= 0 && Math.abs(lastStatsProgress - statsProgress) < progressThreshold) {
            return;
          }

          // 记录当前进度
          lastStatsProgress = statsProgress;

          // 位移动画：从下方 100% → 0，同时淡入显示
          // 使用 immediateRender: false 避免回弹
          gsap.set(statsSection, {
            opacity: statsProgress,
            yPercent: 100 * (1 - statsProgress),
            force3D: true,
            immediateRender: false
          });

          // 如果已完成，移除 will-change 优化，避免回弹
          if (statsProgress >= 1) {
            statsSection.style.willChange = 'auto';
            // 强制设置为最终状态，避免任何回弹
            requestAnimationFrame(() => {
              gsap.set(statsSection, {
                opacity: 1,
                yPercent: 0,
                force3D: true,
                immediateRender: false
              });
            });
          } else {
            statsSection.style.willChange = 'transform, opacity';
          }

          // 更新数字从 min → max
          statNumbers.forEach((el, index) => {
            const min = parseFloat(el.dataset.min) || 0;
            const max = parseFloat(el.dataset.max) || 0;
            const value = Math.round(min + (max - min) * statsProgress);

            if (lastStatValues[index] !== value) {
              el.textContent = value;
              lastStatValues[index] = value;
            }
          });

          // 标记完成状态
          if (statsProgress >= 1) {
            isStatsComplete = true;
          }
        };

        // 计算数字滚动的持续时间，用于调整 ScrollTrigger 的 end
        const statsDuration = parseFloat(statsSection.dataset.statsDuration) || CONFIG.stats.defaultDuration;
        const statsScrollDistance = window.innerHeight * statsDuration;

        // 创建 ScrollTrigger 用于数字滚动
        // 注意：scroll-video-section 的 pin 由 setupCarouselPin 中的逻辑控制
        // 当 scroll-video-section 完全显示后，会通过调整 pinTrigger 来 pin 它
        ScrollTrigger.create({
          trigger: carouselWrapper || section,
          start: carouselWrapper ? 'bottom top' : 'top top',
          end: () => `+=${statsScrollDistance}`, // 与 pinTrigger 的 end 同步
          scrub: isMobile ? 2 : 3, // 进一步增加 scrub 值，让动画更平滑，避免回弹
          scroller: document.body,
          invalidateOnRefresh: true,
          anticipatePin: 1, // 提前准备，减少卡顿
          onEnter: () => {
            // 重置数字滚动状态
            isStatsComplete = false;
            lastStatsProgress = -1;
            lastStatValues = [];
            statNumbers.forEach(el => {
              const min = parseFloat(el.dataset.min) || 0;
              el.textContent = Math.round(min);
            });
            // 重置数字滚动 section 的位置和透明度
            gsap.set(statsSection, {
              opacity: 0,
              yPercent: 100,
              force3D: true
            });
          },
          onEnterBack: () => {
            // 向上回滚进入区间时，重置为隐藏态，避免已消失内容回弹到原位
            isStatsComplete = false;
            lastStatsProgress = -1;
            lastStatValues = [];
            statNumbers.forEach(el => {
              const min = parseFloat(el.dataset.min) || 0;
              el.textContent = Math.round(min);
            });
            gsap.set(statsSection, {
              opacity: 0,
              yPercent: 100,
              force3D: true
            });
          },
          onUpdate: (self) => {
            // 只更新数字滚动
            const progress = Utils.clamp(self.progress || 0, 0, 1);
            updateStats(progress);
          },
          onLeave: () => {
            // 确保数字滚动完成后保持最终状态，避免回弹
            isStatsComplete = true;
            gsap.set(statsSection, {
              opacity: 1,
              yPercent: 0,
              force3D: true
            });
            statNumbers.forEach(el => {
              const min = parseFloat(el.dataset.min) || 0;
              const max = parseFloat(el.dataset.max) || 0;
              el.textContent = Math.round(max);
            });
          },
          onLeaveBack: () => {
            // 向上离开该区间后保持隐藏，防止标题和计数框再次出现
            isStatsComplete = false;
            lastStatsProgress = -1;
            lastStatValues = [];
            gsap.set(statsSection, {
              opacity: 0,
              yPercent: 100,
              force3D: true
            });
            statNumbers.forEach(el => {
              const min = parseFloat(el.dataset.min) || 0;
              el.textContent = Math.round(min);
            });
          }
        });
      });
    }
  }

  // ==================== YouTube 弹窗 ====================
  class YouTubeModal {
    constructor() {
      this.init();
    }

    init() {
      document.addEventListener('click', (e) => this.handleClick(e));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          this.closeActiveModal();
        }
      });

      const descriptionEls = document.querySelectorAll('.mt-text-pin-description[data-youtube-url]');
      if (!descriptionEls.length) return;

      descriptionEls.forEach(el => {
        el.style.cursor = 'pointer';
      });
    }

    handleClick(e) {
      const desc = e.target.closest('.mt-text-pin-description[data-youtube-url]');
      if (desc) {
        const url = desc.getAttribute('data-youtube-url');
        const videoType = desc.getAttribute('data-video-type') || 'video';
        const section = desc.closest('.mt-video-pin-section');
        const sectionId = section?.getAttribute('data-section-id');
        this.openModal(url, sectionId, videoType);
        return;
      }

      if (e.target.closest('.mt-video-modal-close')) {
        const overlay = e.target.closest('.mt-video-modal-overlay');
        this.closeModal(overlay);
        return;
      }

      if (e.target.classList.contains('mt-video-modal-overlay')) {
        this.closeModal(e.target);
      }
    }

    openModal(rawUrl, sectionId, videoType) {
      if (!rawUrl) return;

      const url = rawUrl.trim();
      if (!url) return;

      const type = videoType || 'video';
      const overlayId = sectionId ? `mt-video-modal-overlay-${sectionId}` : null;
      const overlay = overlayId
        ? document.getElementById(overlayId)
        : document.querySelector('.mt-video-modal-overlay');

      if (!overlay) return;

      const content = overlay.querySelector('.mt-video-modal-content');
      if (!content) return;

      content.innerHTML = '';

      if (type === 'video') {
        const video = document.createElement('video');
        video.src = url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        video.preload = 'metadata';
        Object.assign(video.style, {
          width: '100%',
          maxWidth: '100%',
          maxHeight: '80vh',
          borderRadius: '8px',
          background: '#000',
          display: 'block',
          objectFit: 'contain'
        });
        video.addEventListener('error', () => {
          content.innerHTML = '<p style="color: white; padding: 20px; text-align: center;">视频加载失败</p>';
        });
        content.appendChild(video);
      } else {
        let embedUrl = url;
        try {
          const u = new URL(url);
          if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
            let videoId = '';
            if (u.hostname.includes('youtu.be')) {
              videoId = u.pathname.replace('/', '');
            } else if (u.searchParams.get('v')) {
              videoId = u.searchParams.get('v');
            }
            if (videoId) {
              embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1`;
            }
          }
        } catch (e) {
          // 不是合法 URL，使用原始字符串
        }

        const iframe = document.createElement('iframe');
        iframe.src = embedUrl;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        content.appendChild(iframe);
      }

      overlay.classList.add('is-active');
      document.body.classList.add('mt-no-scroll');
      if (typeof lenisInstance !== 'undefined' && lenisInstance?.stop) {
        lenisInstance.stop();
      }
    }

    closeModal(overlay) {
      if (!overlay) return;
      const content = overlay.querySelector('.mt-video-modal-content');
      if (content) {
        content.innerHTML = '';
      }
      overlay.classList.remove('is-active');
      document.body.classList.remove('mt-no-scroll');
      if (typeof lenisInstance !== 'undefined' && lenisInstance?.start) {
        lenisInstance.start();
      }
    }

    closeActiveModal() {
      const activeOverlay = document.querySelector('.mt-video-modal-overlay.is-active');
      if (activeOverlay) {
        this.closeModal(activeOverlay);
      }
    }
  }

  // ==================== 导航栏功能 ====================
  class NavigationManager {
    constructor() {
      this.menu = document.querySelector('.mt-index-nav-right');
      this.footer = document.querySelector('footer');
      this.nav = document.querySelector('.mt-index-nav');
      this.init();
    }

    init() {
      if (this.menu) {
        this.menu.addEventListener('click', () => {
          this.menu.classList.toggle('active');
        });
      }

      // 确保导航菜单始终显示
      if (this.nav) {
        this.nav.style.opacity = '1';
        this.nav.style.pointerEvents = 'auto';
      }
    }
  }

  // ==================== 初始化 ====================
  const controller = new VideoPinSectionController();
  const youtubeModal = new YouTubeModal();
  const navigationManager = new NavigationManager();

  function scrollToTop() {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    if (controller.lenisManager.instance) {
      controller.lenisManager.instance.scrollTo(0, { immediate: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scrollToTop();
      controller.init();
    });
  } else {
    scrollToTop();
    controller.init();
  }

  window.addEventListener('load', () => {
    scrollToTop();
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 300);
  });

  window.addEventListener('beforeunload', () => {
    window.scrollTo(0, 0);
    if (controller.lenisManager.instance) {
      controller.lenisManager.instance.scrollTo(0, { immediate: true });
    }
  });

  // 导出全局变量供其他脚本使用
  window.lenisInstance = null; // 将在 LenisManager.init() 中设置
  Object.defineProperty(window, 'lenisInstance', {
    get() {
      return controller.lenisManager.instance;
    }
  });

})();
